import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SelectPanelWidthDirective } from '../../../../_helpers/select-panel-width.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subject, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Lookup } from 'src/app/models/lookup.model';
import { SampleSurveyTypeService } from 'src/app/services/sample-survey-type.service';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { SegProfileAgentResponse } from 'src/app/models/seg-profile-summary.model';
import {
    SegProfileFilterValues,
    SEG_PROFILE_FILTER_DEFAULTS,
    DATE_RANGE_OPTIONS,
} from './seg-profile-summary-widget.constants';
import { SearchableList } from '../_shared/searchable-list';
import { MultiSelectClearComponent } from '../_shared/multi-select-clear/multi-select-clear.component';
import { CustomDateRangeController } from '../_shared/custom-date-range-controller';
import { buildDateRangeOptionsWithRange } from '../_shared/date-filter.util';

@Component({
    standalone: true,
    selector: 'app-seg-profile-summary-filters',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    NgxMatSelectSearchModule,
    MultiSelectClearComponent,
    SelectPanelWidthDirective
],
    templateUrl: './seg-profile-summary-filters.component.html',
    styleUrls: ['./seg-profile-summary-filters.component.scss']
})
export class SegProfileSummaryFiltersComponent implements OnInit, OnChanges, OnDestroy {
    @Input() values: SegProfileFilterValues = { ...SEG_PROFILE_FILTER_DEFAULTS };
    @Input() compact = false;
    @Input() availableAgents: SegProfileAgentResponse[] = [];

    @Output() filterChange = new EventEmitter<SegProfileFilterValues>();

    dateRangeOptionsWithRange: { value: number; displayName: string }[] = [];
    readonly DateFilterTypeEnum = DateFilterTypeEnum;

    customDateLabel = '';

    surveyTypes = new SearchableList<Lookup>(
        (t, q) => !!t.name?.toLowerCase().includes(q),
        () => this.values.surveyTypeIds,
    );

    private readonly destroy$ = new Subject<void>();
    private readonly dateRangeCtrl: CustomDateRangeController;

    constructor(
        dialog: MatDialog,
        private readonly dateFormatService: DateFormatService,
        private readonly translate: TranslateService,
        private readonly cdr: ChangeDetectorRef,
        private readonly sampleSurveyTypeService: SampleSurveyTypeService,
    ) {
        this.dateRangeCtrl = new CustomDateRangeController(dialog, dateFormatService, translate);
    }

    ngOnInit(): void {
        this.buildDateRangeOptions();
        this.dateRangeCtrl.syncFromStrings(this.values.customStartDate, this.values.customEndDate);
        this.updateCustomDateLabel();
        this.loadSurveyTypes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['values']) {
            this.surveyTypes.syncSelection();
        }
    }

    private updateCustomDateLabel(): void {
        this.customDateLabel = this.dateRangeCtrl.formatLabel(this.values.customStartDate, this.values.customEndDate);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onAgentChange(value: string | null): void {
        this.values = { ...this.values, agentId: value };
        this.filterChange.emit(this.values);
    }

    onSurveyTypesChange(value: string[]): void {
        this.values = { ...this.values, surveyTypeIds: value };
        this.surveyTypes.syncSelection();
        this.filterChange.emit(this.values);
    }

    onDateFilterChange(value: number | undefined): void {
        if (this.values.dateFilterType === value) return;
        const prev = this.values.dateFilterType;
        this.values = { ...this.values, dateFilterType: value };

        if (value === DateFilterTypeEnum.Custom) {
            this.openCustomDateDialog(prev);
            return;
        }

        this.values = { ...this.values, customStartDate: null, customEndDate: null };
        this.dateRangeCtrl.syncFromStrings(null, null);
        this.updateCustomDateLabel();
        this.filterChange.emit(this.values);
    }

    openCustomDateDialog(previousDateFilterType: number | undefined): void {
        this.dateRangeCtrl.openDialog().pipe(takeUntil(this.destroy$)).subscribe({
            next: result => {
                if (!result) {
                    this.values = { ...this.values, dateFilterType: previousDateFilterType };
                    this.filterChange.emit(this.values);
                    this.cdr.markForCheck();
                    return;
                }
                const { startIso, endIso } = this.dateRangeCtrl.applyResult(result);
                this.values = {
                    ...this.values,
                    dateFilterType: DateFilterTypeEnum.Custom,
                    customStartDate: startIso,
                    customEndDate: endIso,
                };
                this.updateCustomDateLabel();
                this.filterChange.emit(this.values);
                this.cdr.markForCheck();
            },
        });
    }

    private loadSurveyTypes(): void {
        this.sampleSurveyTypeService.getAll('').pipe(
            catchError(() => of(null)),
            takeUntil(this.destroy$),
        ).subscribe({
            next: surveyTypes => {
                this.surveyTypes.setItems(surveyTypes ?? []);
                const valid = surveyTypes ? this.surveyTypes.prune(this.values.surveyTypeIds) : null;
                if (valid) {
                    this.values = { ...this.values, surveyTypeIds: valid };
                    this.surveyTypes.syncSelection();
                    this.filterChange.emit(this.values);
                }
                this.cdr.markForCheck();
            },
        });
    }

    private buildDateRangeOptions(): void {
        this.dateRangeOptionsWithRange = buildDateRangeOptionsWithRange(
            DATE_RANGE_OPTIONS,
            this.translate,
            this.dateFormatService,
        );
    }

    public trackByIndex = trackByIndex;
}
